import { cn } from '../../lib/cn';
import { Card } from '../design-system/Card';
import { Text } from '../design-system/Text';
import { Checkbox } from '../design-system/Checkbox';
import { ShieldCheck, Shield } from 'lucide-react';
import { formatCurrency, formatDateRelative } from '../../utils/format';
import type { Challan } from '../../types';

interface ChallanCardProps {
  challan: Challan;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export function ChallanCard({ challan, selected, onToggle, disabled }: ChallanCardProps) {
  const isPaid = challan.status === 'paid';

  return (
    <Card
      padding="md"
      elevation={selected ? 2 : 1}
      className={cn(
        'mx-4 transition-colors',
        selected && 'border-park-200 bg-park-50 dark:border-park-800 dark:bg-park-900/20',
        isPaid && 'opacity-70'
      )}
      data-testid={`challan-card-${challan.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {isPaid ? (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
              <ShieldCheck className="h-4 w-4" />
            </div>
          ) : (
            <Checkbox
              checked={selected}
              onChange={onToggle}
              disabled={disabled || isPaid}
              aria-label={`Select challan ${challan.violation}`}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <Text variant="body-medium" className="truncate">{challan.violation}</Text>
              <Text variant="caption" color="secondary">{challan.authority}</Text>
            </div>
            <Text variant="body-medium" color={isPaid ? 'success' : 'primary'} className="shrink-0">
              {formatCurrency(challan.amount)}
            </Text>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <Text variant="caption" color="tertiary">{formatDateRelative(challan.date)}</Text>
            {isPaid ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <ShieldCheck className="h-3 w-3" /> Paid
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <Shield className="h-3 w-3" /> Pending
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
